<script setup lang="ts">
import { isTouchScreen } from '@@/bits/browser'
import { isAppUsing } from '@@/bits/flip'
import type { ToolTailwindColorType } from '@@/bits/whiteboard_tools'
import OzDivider, { OzDividerColorScheme, OzDividerOrientation } from '@@/library/v4/components/OzDivider.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import type OzIconButton from '@@/library/v4/components/OzIconButton.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import OzTextLink, { OzTextLinkColorPreset } from '@@/library/v4/components/OzTextLink.vue'
import OzTooltip from '@@/library/v4/components/OzTooltip.vue'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardLinkingStore } from '@@/pinia/whiteboard_linking'
import type { WhiteboardSecondaryMenu, WhiteboardTool } from '@@/types/whiteboard'
import WhiteboardSecondaryMenuIcon from '@@/vuecomponents/WhiteboardSecondaryMenuIcon.vue'
import { offset, shift } from '@floating-ui/vue'
import { computed, ref } from 'vue'

// Props
const props = withDefaults(
  defineProps<{
    mode: 'toolbar' | 'toolbarHorizontal'
    colorScheme: 'dark' | 'light'
    secondaryMenus: WhiteboardSecondaryMenu[]
    secondarySelectionIds?: string[]
    iconsPerLine?: 1 | 3
    selectedShapeColor?: ToolTailwindColorType
    shapeId?: string
  }>(),
  {
    secondarySelectionIds: undefined,
    iconsPerLine: 3,
    selectedShapeColor: 'dark-text-100',
    shapeId: undefined,
  },
)
// Emit setup
const emit = defineEmits(['select'])
function handleSelection(item: { id: string }, secondaryMenuId: string, $event: PointerEvent): void {
  emit('select', item, secondaryMenuId, $event)
}

const whiteboardLinkingStore = useWhiteboardLinkingStore()
const whiteboardStore = useWhiteboardStore()
const xLinkingWarningTooltip = ref<boolean>(false)
const warningIconRef = ref<InstanceType<typeof OzIconButton>[]>()
const warningIconEl = computed(() => warningIconRef.value?.[0].$el)
const currentLinkingError = computed(() => {
  if (!props.shapeId) return ''
  return whiteboardLinkingStore.linkError(props.shapeId)
})

const getItemIconColor = (
  secondaryMenu: WhiteboardSecondaryMenu,
  item: WhiteboardTool,
  isSelected: boolean,
): string | undefined => {
  if (item.id === 'colorMenu') return props.selectedShapeColor
  if (item.id === 'highlight' && isSelected) return 'dark-text-100'
  if (secondaryMenu.id === 'color' || item.iconName === 'color') return item.id
  if (secondaryMenu.id === 'fill-style' && props.mode === 'toolbar') return props.secondarySelectionIds?.[0]
  return undefined
}

/**
 * RESPONSIVE
 */

const isTouch = isTouchScreen()
</script>

<template>
  <div
    :style="
      isAppUsing('sandboxCardNavigation')
        ? { background: whiteboardStore.frameBarAndToolbarBackground, backdropFilter: 'blur(6px)' }
        : undefined
    "
    :class="[
      // Color
      !isAppUsing('sandboxCardNavigation') && {
        'bg-popover-dark': colorScheme === 'dark',
        'bg-popover-light': colorScheme === 'light',
        'backdrop-blur-10': true,
      },
      'will-change-auto',

      // Shape
      {
        'rounded-xl': iconsPerLine === 3,
        'rounded-full': iconsPerLine === 1,
      },
      {
        'py-2': mode === 'toolbar' && iconsPerLine === 3,
        'py-1': mode === 'toolbar' && iconsPerLine === 1,
        'px-2': mode === 'toolbarHorizontal' && iconsPerLine === 3,
        'px-1': mode === 'toolbarHorizontal' && iconsPerLine === 1,
      },

      // Children positioning
      'flex',
      {
        'flex-col': mode === 'toolbar',
        'flex-row': mode === 'toolbarHorizontal',
      },
      'items-center',
      'justify-center',
      {
        'gap-2.5': iconsPerLine === 3 && !isTouch,
        'gap-2': iconsPerLine === 1 || (iconsPerLine === 3 && isTouch),
      },
    ]"
  >
    <div
      v-for="(secondaryMenu, index) in secondaryMenus"
      :key="secondaryMenu.id"
      :class="mode === 'toolbar' ? 'flex flex-col' : 'flex flex-row'"
    >
      <div
        :class="
          mode === 'toolbar'
            ? [
                'grid',
                {
                  'grid-cols-1': iconsPerLine === 1,
                  'grid-cols-3': iconsPerLine === 3,
                },
                {
                  'gap-1': iconsPerLine === 1,
                  'gap-2.5': iconsPerLine === 3,
                },
                {
                  'mx-1': iconsPerLine === 1,
                  'mx-2.5': iconsPerLine === 3,
                },
              ]
            : [
                'grid',
                {
                  'grid-rows-1 grid-flow-col': iconsPerLine === 1,
                  'grid-rows-3 grid-flow-col': iconsPerLine === 3,
                },
                {
                  'gap-1':
                    iconsPerLine === 1 && !(props.shapeId || whiteboardLinkingStore.checkIfLinkingExists(shapeId)),
                  'gap-0.5':
                    iconsPerLine === 1 && props.shapeId && whiteboardLinkingStore.checkIfLinkingExists(shapeId),
                  'gap-2.5': iconsPerLine === 3,
                },
                {
                  'my-1': iconsPerLine === 1,
                  'my-2.5': iconsPerLine === 3 && !isTouch,
                  'my-2': iconsPerLine === 3 && isTouch,
                },
              ]
        "
      >
        <template v-for="item in secondaryMenu.items">
          <WhiteboardSecondaryMenuIcon
            :key="item.id"
            :is-selected="item.id === secondarySelectionIds[index]"
            :color-scheme="colorScheme"
            :icon-name="item.iconName"
            :icon-color="getItemIconColor(secondaryMenu, item, item.id === secondarySelectionIds[index])"
            :selected-background-color="secondaryMenu.id === 'draw-tool' ? secondarySelectionIds[1] : undefined"
            :mode="mode"
            :label="item.label"
            @click="handleSelection(item, secondaryMenu.id, $event)"
          />
          <template v-if="item.id === 'linkedMenu'">
            <OzPlainButton
              v-if="currentLinkingError"
              :key="`${item.id}WarningIcon`"
              ref="warningIconRef"
              :size-preset="OzPlainButtonSizePreset.H24px"
              :color-scheme="OzPlainButtonColorScheme.Bare"
              :aria-label="currentLinkingError"
              :dark-mode="colorScheme === 'dark'"
              :class="['text-danger-100', 'px-1', { 'mt-0.5': !isTouch, 'mt-1.5': isTouch }]"
              @focus="xLinkingWarningTooltip = true"
              @blur="xLinkingWarningTooltip = false"
              @mouseenter="xLinkingWarningTooltip = true"
              @mouseleave="xLinkingWarningTooltip = false"
              @touchstart="xLinkingWarningTooltip = !xLinkingWarningTooltip"
              @click="xLinkingWarningTooltip = !xLinkingWarningTooltip"
            >
              <OzIcon :size="20" name="warning" />
            </OzPlainButton>

            <OzTextLink
              :key="`${item.id}Text`"
              :class="['bg-transparent', '!ring-offset-light-ui-400', 'p-0 me-1']"
              :color-preset="OzTextLinkColorPreset.Secondary"
              :dark-mode="false"
              :text="whiteboardLinkingStore.getLinkingText(shapeId)"
              data-testid="whiteboardLinkText"
              @click="handleSelection({ id: 'triggerLink' }, secondaryMenu.id, $event)"
            />
          </template>
          <OzDivider
            v-if="item.xDivider"
            :key="`${item.id}Divider`"
            :dark-mode="false"
            :color-scheme="OzDividerColorScheme.Solid"
            :orientation="OzDividerOrientation.Vertical"
            class="!w-[0.5px] !h-[20px] mt-1 mx-0.5"
          />
        </template>
      </div>
      <OzDivider
        v-if="index < secondaryMenus.length - 1"
        :key="`divider-${index}`"
        :color-scheme="OzDividerColorScheme.Solid"
        :orientation="mode === 'toolbar' ? OzDividerOrientation.Horizontal : OzDividerOrientation.Vertical"
        :dark-mode="colorScheme === 'dark'"
        :class="
          mode === 'toolbar'
            ? [
                'ms-1.5',
                'me-1.5',
                {
                  'mt-2.5': iconsPerLine === 3,
                  'mt-2': iconsPerLine === 1,
                },
              ]
            : [
                'mt-1.5',
                'mb-1.5',
                {
                  'ms-2.5': iconsPerLine === 3 && !isTouch,
                  'ms-2': iconsPerLine === 1 || (iconsPerLine === 3 && isTouch),
                },
              ]
        "
      />
    </div>
    <OzTooltip
      v-if="warningIconEl && xLinkingWarningTooltip && currentLinkingError"
      :anchor="warningIconEl"
      :class="'z-50'"
      :dark-mode="false"
      :config="{
        placement: 'top-start',
        middleware: [
          offset({
            // Move the popover up by 8px so it doesn't obstruct the tooltip button.
            mainAxis: 8,
          }),
          shift(),
        ],
      }"
      class="max-w-xs whitespace-nowrap"
    >
      <p>{{ currentLinkingError }}</p>
    </OzTooltip>
  </div>
</template>
